/**
 * Carte de contrôle de l'aperçu.
 *
 * Volontairement plus simple que `ControlChart` : ici on montre ce que le fichier
 * contient, pas une caractéristique configurée. Pas de zones A/B/C ni de spécifications —
 * elles n'existent pas encore, et les afficher vides donnerait une fausse impression de
 * complétude.
 *
 * Les couleurs sont lues sur le thème courant plutôt qu'écrites en dur : un canvas ne
 * connaît ni les variables CSS ni la classe `dark`, il faut les lui donner. Les
 * redéclarer ici les ferait diverger de l'interface à la première retouche de palette.
 */

import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";
import { useMemo } from "react";

import type { AnalysedChart } from "./mappingApi";
import { useChartPalette } from "./ui/theme";

export function PreviewChart({
  chart,
  height = 300,
}: {
  chart: AnalysedChart;
  height?: number;
}) {
  const palette = useChartPalette();

  const option = useMemo<EChartsOption>(() => {
    const flagged = new Set(chart.violation_indexes);
    const data = chart.points.map((value, i) => ({
      value,
      itemStyle: flagged.has(i)
        ? { color: palette.bad, borderColor: palette.surface, borderWidth: 1 }
        : undefined,
      symbolSize: flagged.has(i) ? 7 : 0,
    }));

    const line = (
      value: number,
      color: string,
      label: string,
      dashed: boolean,
    ) => ({
      yAxis: value,
      lineStyle: {
        color,
        width: 1,
        type: dashed ? ("dashed" as const) : ("solid" as const),
      },
      label: {
        formatter: label,
        position: "insideEndTop" as const,
        color,
        fontSize: 10,
        fontFamily: "ui-monospace, monospace",
      },
    });

    // Au-delà de quelques centaines de points, le tracé point par point devient une
    // bouillie : on garde la ligne et on ne matérialise que les violations.
    const dense = chart.points.length > 400;

    return {
      animation: true,
      animationDuration: 320,
      textStyle: {
        color: palette.muted,
        fontFamily: "Inter, system-ui, sans-serif",
      },
      tooltip: {
        trigger: "axis",
        backgroundColor: palette.surface,
        borderColor: palette.grid,
        textStyle: { color: palette.ink, fontSize: 12 },
      },
      grid: { left: 56, right: 24, top: 16, bottom: 44 },
      xAxis: {
        type: "category",
        data: chart.points.map((_, i) => String(i + 1)),
        axisLine: { lineStyle: { color: palette.grid } },
        axisTick: { show: false },
        axisLabel: { color: palette.muted, fontSize: 10 },
      },
      yAxis: {
        type: "value",
        scale: true,
        splitLine: { lineStyle: { color: palette.grid } },
        axisLabel: { color: palette.muted, fontSize: 10 },
      },
      dataZoom: [
        { type: "inside" },
        {
          type: "slider",
          height: 16,
          bottom: 6,
          borderColor: palette.grid,
          fillerColor: `${palette.accent}22`,
          handleStyle: { color: palette.accent },
          textStyle: { color: palette.muted, fontSize: 9 },
        },
      ],
      series: [
        {
          name: chart.characteristic_code,
          type: "line",
          data,
          smooth: false,
          sampling: dense ? "lttb" : undefined,
          lineStyle: { color: palette.accent, width: 1.2 },
          itemStyle: { color: palette.accent },
          symbol: "circle",
          symbolSize: dense ? 0 : 4,
          markLine: {
            silent: true,
            symbol: "none",
            data: [
              line(chart.upper, palette.bad, "LCS", true),
              line(chart.center, palette.good, "Moyenne", false),
              line(chart.lower, palette.bad, "LCI", true),
            ],
          },
        },
      ],
    };
  }, [chart, palette]);

  return (
    <ReactECharts
      option={option}
      style={{ height, width: "100%" }}
      opts={{ renderer: "canvas" }}
      notMerge
    />
  );
}
